import { useCallback, useEffect, useRef, useState } from 'react';
import { authFetch } from '../util/authFetch';
import { handleUnauthorizedRedirect } from '../util/authRedirect';
// ============================================================================
// Base Hook
// ============================================================================
function useBaseFetch(url, method, options = {}) {
    const { enabled = true, initialData = null, parseJson, skipAuth = false } = options;
    const [data, setData] = useState(initialData);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState(null);
    const abortRef = useRef(null);
    const execute = useCallback(async (body) => {
        if (!url)
            return null;
        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;
        setLoading(true);
        setError(null);
        try {
            const headers = {};
            // Add Content-Type for methods with JSON body
            if (body !== undefined && (method === 'POST' || method === 'PUT' || method === 'PATCH')) {
                headers['Content-Type'] = 'application/json';
            }
            const fetchOptions = {
                method,
                headers,
                signal: controller.signal,
            };
            // Add body for methods with payload
            if (body !== undefined) {
                fetchOptions.body = JSON.stringify(body);
            }
            const response = skipAuth
                ? await fetch(url, fetchOptions)
                : await authFetch(url, fetchOptions);
            setStatus(response.status);
            const rawText = await response.text();
            const contentType = response.headers.get('content-type') || '';
            const shouldParseJson = parseJson ?? contentType.includes('application/json');
            let payload = rawText;
            if (shouldParseJson && rawText) {
                try {
                    payload = JSON.parse(rawText);
                }
                catch {
                    payload = rawText;
                }
            }
            if (!response.ok) {
                if (!skipAuth && response.status === 401) {
                    handleUnauthorizedRedirect();
                }
                const objectMessage = payload && typeof payload === 'object'
                    ? (payload.message ||
                        payload.error ||
                        payload.errors?.[0])
                    : undefined;
                const message = typeof payload === 'string'
                    ? payload
                    : objectMessage || response.statusText || `Request failed: ${response.status}`;
                setError(message || `Request failed: ${response.status}`);
                return null;
            }
            setData(payload);
            return payload;
        }
        catch (err) {
            if (err instanceof DOMException && err.name === 'AbortError') {
                return null;
            }
            const errorMessage = err instanceof Error ? err.message : 'Request failed';
            setError(errorMessage);
            return null;
        }
        finally {
            setLoading(false);
        }
    }, [url, method, skipAuth, parseJson]);
    // Clear stale data and abort in-flight requests when URL changes
    const prevUrlRef = useRef(url);
    useEffect(() => {
        if (prevUrlRef.current !== url) {
            prevUrlRef.current = url;
            setData(initialData);
            setError(null);
            setStatus(null);
        }
        return () => {
            abortRef.current?.abort();
        };
    }, [enabled, url, initialData]);
    return { execute, data, error, loading, status };
}
// ============================================================================
// HTTP Method Hooks
// ============================================================================
/**
 * GET request hook - auto-executes on mount when enabled
 */
export function useGet(url, options = {}) {
    const result = useBaseFetch(url, 'GET', options);
    const { enabled = true } = options;
    useEffect(() => {
        if (enabled && url) {
            result.execute();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, url]);
    return result;
}
/**
 * POST request hook - manual execute with body
 */
export function usePost(url, options = {}) {
    return useBaseFetch(url, 'POST', options);
}
/**
 * PATCH request hook - manual execute with body
 */
export function usePatch(url, options = {}) {
    return useBaseFetch(url, 'PATCH', options);
}
/**
 * PUT request hook - manual execute with body
 */
export function usePut(url, options = {}) {
    return useBaseFetch(url, 'PUT', options);
}
/**
 * DELETE request hook - manual execute
 */
export function useDelete(url, options = {}) {
    return useBaseFetch(url, 'DELETE', options);
}
// ============================================================================
// Legacy useFetch (backwards compatibility)
// ============================================================================
/**
 * @deprecated Use useGet, usePost, usePut, useDelete instead
 */
export function useFetch(url, options = {}) {
    const { method = 'GET', ...rest } = options;
    const httpMethod = method.toUpperCase() || 'GET';
    return useBaseFetch(url, httpMethod, rest);
}
